import type { ITransaction } from "../ITransaction";

/**
 * Built-in CRUD + structural permissions used across entities/atoms/fields.
 */
export enum GenericPermission {
  Create = "create",
  Update = "update",
  Delete = "delete",
  Read = "read",
  Rename = "rename",
  Move = "move",
  CreateChildren = "createChildren",
  DeleteChildren = "deleteChildren",
  UpdateChildren = "updateChildren",
}

export type ReducerPermissions<ReducerName extends string = string> =
  `reducer:${ReducerName}`;
/**
 * Project-specific explicit write permissions.
 * Example: `custom:UpdateProtectedUserFields`
 */
export type CustomPermissions<CustomName extends string = string> =
  `custom:${CustomName}`;

export type EntityPermissions<EntityType extends string = string> =
  `${EntityType}/${GenericPermission}`;
export type AtomPermissions<AtomType extends string = string> =
  `${AtomType}/${GenericPermission}`;
export type FieldPermissions<Field extends string = string> =
  `${Field}/${GenericPermission}`;

export type ValidProjectSpecificPermissions<
  EntityType extends string = string,
  AtomType extends string = string,
  ReducerName extends string = string,
  CustomName extends string = string,
> =
  | EntityPermissions<EntityType>
  | AtomPermissions<AtomType>
  | FieldPermissions
  | ReducerPermissions<ReducerName>
  | CustomPermissions<CustomName>;

/**
 * Build a reducer permission string (`reducer:<name>`).
 */
export function makeReducerPermission({
  reducerName,
}: {
  reducerName: string;
}): ReducerPermissions {
  return `reducer:${reducerName}`;
}

/**
 * Build a custom permission string (`custom:<name>`).
 * Useful for `@requireExplicitWritePermission <Name>` checks.
 */
export function makeCustomPermission({
  permissionName,
}: {
  permissionName: string;
}): CustomPermissions {
  return `custom:${permissionName}`;
}

/**
 * Build entity / atom / field permission strings with validation.
 */
export function makePermission({
  permission,
  entityType,
  atomType,
  field,
}: {
  permission: GenericPermission;
  entityType: string;
  atomType?: string;
  field?: string;
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
}): ValidProjectSpecificPermissions<any, any, any> {
  if (field && !atomType) {
    throw new Error("field-level permissions require atomType");
  }

  if (
    atomType &&
    (permission === GenericPermission.Rename ||
      permission === GenericPermission.Move ||
      (!field &&
        (permission === GenericPermission.Create ||
          permission === GenericPermission.Delete)))
  ) {
    throw new Error(
      "create, delete, rename, or move permission is only allowed on entities",
    );
  }

  if (field) {
    return `${entityType}:${atomType}:${field}/${permission}` as ValidProjectSpecificPermissions<
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
      any,
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
      any,
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
      any
    >;
  }
  if (atomType) {
    return `${entityType}:${atomType}/${permission}` as ValidProjectSpecificPermissions<
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
      any,
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
      any,
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
      any
    >;
  }
  return `${entityType}/${permission}` as ValidProjectSpecificPermissions<
    // biome-ignore lint/suspicious/noExplicitAny: legacy code
    any,
    // biome-ignore lint/suspicious/noExplicitAny: legacy code
    any,
    // biome-ignore lint/suspicious/noExplicitAny: legacy code
    any
  >;
}

// export type FieldPermissions =
//   | GenericPermission[]
//   | Partial<Record<string, GenericPermission[]>>;

// export type AtomPermissions =
//   | GenericPermission[]
//   | Partial<Record<string, FieldPermissions>>;

// export type EntityPermissions<AtomType extends string> = GenericPermission[];

// export type RolePermissions<
//   AtomType extends string,
//   EntityType extends string,
// > = Partial<
//   | Record<EntityType, EntityPermissions<AtomType>>
//   | Record<AtomType, AtomPermissions>
// >;

// export type PermissionsByRole<
//   RoleType extends string,
//   EntityType extends string,
//   AtomType extends string,
// > = Partial<Record<RoleType, RolePermissions<AtomType, EntityType>>>;

export type RoleCheckingPermissions = ValidProjectSpecificPermissions[];

export type GetRolesFromContext<RoleType extends string> = (
  transaction: ITransaction,
) => Promise<RoleType[]>;

export type PermissionsFromRoles<
  RoleType extends string,
  PermissionsType extends ValidProjectSpecificPermissions,
> = (
  permissionsByRoles: PermissionsByRole<RoleType, PermissionsType>,
  roles: RoleType[],
) => Set<PermissionsType>;

export type CheckAtomPermission<AtomType extends string> = (
  atomType: AtomType,
  permission: GenericPermission,
) => boolean;

export type CheckFieldPermission<
  AtomType extends string = string,
  FieldType extends string = string,
> = (
  atomType: AtomType,
  field: FieldType,
  permission: GenericPermission,
) => boolean;

export type CheckEntityPermission<EntityType extends string> = (
  entityType: EntityType,
  permission: GenericPermission,
) => boolean;

export type CheckReducerPermission = (
  perm:
    | ReducerPermissions
    | CustomPermissions
    | Array<ReducerPermissions | CustomPermissions>,
) => boolean;

export type PermissionsByRole<
  RoleType extends string,
  PermissionsType extends ValidProjectSpecificPermissions,
> = Partial<Record<RoleType, PermissionsType[]>>;

export function entityPermission(
  entity: string,
  permission: GenericPermission,
): EntityPermissions {
  return makePermission({
    entityType: entity,
    permission,
  }) as EntityPermissions;
}

export function readEntityPermission(entity: string): EntityPermissions {
  return entityPermission(entity, GenericPermission.Read);
}

export function createEntityPermission(entity: string): EntityPermissions {
  return entityPermission(entity, GenericPermission.Create);
}

export function updateEntityPermission(entity: string): EntityPermissions {
  return entityPermission(entity, GenericPermission.Update);
}

export function deleteEntityPermission(entity: string): EntityPermissions {
  return entityPermission(entity, GenericPermission.Delete);
}

export function renameEntityPermission(entity: string): EntityPermissions {
  return entityPermission(entity, GenericPermission.Rename);
}

export function moveEntityPermission(entity: string): EntityPermissions {
  return entityPermission(entity, GenericPermission.Move);
}

export function atomPermission(
  entity: string,
  atomName: string,
  permission: GenericPermission,
): ValidProjectSpecificPermissions {
  return makePermission({
    entityType: entity,
    atomType: atomName,
    permission,
  });
}

export function readAtomPermission(
  entity: string,
  atomName: string,
): ValidProjectSpecificPermissions {
  return atomPermission(entity, atomName, GenericPermission.Read);
}

export function updateAtomPermission(
  entity: string,
  atomName: string,
): ValidProjectSpecificPermissions {
  return atomPermission(entity, atomName, GenericPermission.Update);
}

export function fieldPermission(
  entity: string,
  atomName: string,
  field: string,
  permission: GenericPermission,
): FieldPermissions {
  return makePermission({
    entityType: entity,
    atomType: atomName,
    field,
    permission,
  }) as FieldPermissions;
}

export function allowReadingEntities(entities: string[]): EntityPermissions[] {
  return entities.map(readEntityPermission);
}

export function allowCreatingEntities(entities: string[]): EntityPermissions[] {
  return entities.flatMap((entity) => [
    // creating an entity requires read access
    readEntityPermission(entity),
    createEntityPermission(entity),
  ]);
}

export function allowUpdatingEntities(entities: string[]): EntityPermissions[] {
  return entities.flatMap((entity) => [
    // updating an entity requires read access
    readEntityPermission(entity),
    updateEntityPermission(entity),
  ]);
}

export function allowUpdatingAtoms(
  entity: string,
  atomName: string,
): ValidProjectSpecificPermissions[] {
  return [updateAtomPermission(entity, atomName)];
}

export function allowDeletingEntities(entities: string[]): EntityPermissions[] {
  return entities.map(deleteEntityPermission);
}

export function allowRenamingEntities(entities: string[]): EntityPermissions[] {
  return entities.flatMap((entity) => [
    // renaming an entity requires read access
    readEntityPermission(entity),
    renameEntityPermission(entity),
  ]);
}

export function allowMovingEntities(entities: string[]): EntityPermissions[] {
  return entities.flatMap((entity) => [
    // moving an entity requires read access
    readEntityPermission(entity),
    moveEntityPermission(entity),
  ]);
}

export function grantFieldPermission(
  entity: string,
  atomName: string,
  fields: string[],
  permission: GenericPermission,
): FieldPermissions[] {
  return fields.map((field) =>
    fieldPermission(entity, atomName, field, permission),
  );
}

/**
 * Grant create, update and delete permissions for a set of fields.
 * Intended for fields tagged with `@requireExplicitWritePermission`.
 */
export function grantFieldWritePermissions(
  entity: string,
  atomName: string,
  fields: string[],
): FieldPermissions[] {
  return [
    ...grantFieldPermission(entity, atomName, fields, GenericPermission.Create),
    ...grantFieldPermission(entity, atomName, fields, GenericPermission.Update),
    ...grantFieldPermission(entity, atomName, fields, GenericPermission.Delete),
  ];
}
